import { useEffect, useState } from "react";
import { BASE_URL } from "../utils/constants";

// The server root (the API itself has no handler at "/api" and answers 404,
// which shows up as console noise on every poll). Reaching it proves the server
// is up, and the round trip time is used as the latency reading. The request is
// opaque (no-cors), so only "did the server answer" is observable.
const PING_URL = `${new URL(BASE_URL).origin}/`;

// How often the connection is re-checked while the tab is visible.
const POLL_INTERVAL = 15000;

// A ping that takes longer than this is treated as "server unreachable".
const PING_TIMEOUT = 8000;

// Latency buckets, matching the legend shown in the header tooltip.
export const NETWORK_LEVELS = {
    excellent: { label: "Excellent", color: "#2fd693", condition: "Online, latency < 100 ms" },
    good: { label: "Good", color: "#f5c445", condition: "Online, latency 100–300 ms" },
    slow: { label: "Slow", color: "#fb7a3c", condition: "Online, latency 300–700 ms" },
    poor: { label: "Poor", color: "#e8385c", condition: "Online, latency > 700 ms" },
    offline: { label: "Offline", color: "#2b2b3a", condition: "No internet or server unreachable" },
};

export const NETWORK_LEVEL_ORDER = ["excellent", "good", "slow", "poor", "offline"];

function levelFromLatency(latency) {
    if (latency < 100) return "excellent";
    if (latency < 300) return "good";
    if (latency < 700) return "slow";
    return "poor";
}

// ---------------------------------------------------------------------------
// Shared store. The indicator is rendered several times per page (every header
// has a desktop and a mobile copy), so the ping loop lives here at module level
// and every mounted indicator reads from the same result. One timer, one
// request, no matter how many indicators are on screen.
// ---------------------------------------------------------------------------

let state = {
    level: typeof navigator !== "undefined" && navigator.onLine === false ? "offline" : "good",
    latency: null,
    checkedAt: null,
};

const subscribers = new Set();
let intervalId = null;
let listenersAttached = false;
// Guards against a slow in-flight ping overwriting a newer result.
let requestId = 0;

function setState(next) {
    state = { ...state, ...next };
    subscribers.forEach((notify) => notify(state));
}

async function measure() {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
        setState({ level: "offline", latency: null, checkedAt: Date.now() });
        return;
    }

    const currentRequest = ++requestId;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT);
    const startedAt = performance.now();

    try {
        // no-cors keeps this working when the app is served from a different
        // origin than BASE_URL (local dev against a remote server). The response
        // is opaque, but it only resolves when the server actually replied.
        await fetch(`${PING_URL}?_=${Date.now()}`, {
            method: "HEAD",
            mode: "no-cors",
            cache: "no-store",
            signal: controller.signal,
        });
        const elapsed = Math.round(performance.now() - startedAt);
        if (currentRequest !== requestId) return;
        setState({ level: levelFromLatency(elapsed), latency: elapsed, checkedAt: Date.now() });
    } catch (e) {
        if (currentRequest !== requestId) return;
        setState({ level: "offline", latency: null, checkedAt: Date.now() });
    } finally {
        clearTimeout(timeoutId);
    }
}

function onOnline() {
    measure();
}

function onOffline() {
    setState({ level: "offline", latency: null, checkedAt: Date.now() });
}

function onVisibilityChange() {
    if (document.visibilityState === "visible") measure();
}

function startPolling() {
    if (listenersAttached) return;
    listenersAttached = true;

    measure();
    intervalId = setInterval(() => {
        if (document.visibilityState === "visible") measure();
    }, POLL_INTERVAL);

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVisibilityChange);
}

function stopPolling() {
    if (!listenersAttached) return;
    listenersAttached = false;

    clearInterval(intervalId);
    intervalId = null;
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
    document.removeEventListener("visibilitychange", onVisibilityChange);
}

const useNetworkStatus = () => {
    const [snapshot, setSnapshot] = useState(state);

    useEffect(() => {
        subscribers.add(setSnapshot);
        // First indicator on the page starts the loop.
        if (subscribers.size === 1) startPolling();
        // Adopt whatever the shared store already knows.
        setSnapshot(state);

        return () => {
            subscribers.delete(setSnapshot);
            if (subscribers.size === 0) stopPolling();
        };
    }, []);

    return {
        level: snapshot.level,
        latency: snapshot.latency,
        checkedAt: snapshot.checkedAt,
        isOffline: snapshot.level === "offline",
        ...NETWORK_LEVELS[snapshot.level],
        refresh: measure,
    };
};

export default useNetworkStatus;
